import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PromoterFloorplanView from 'components/PromoterFloorplanView';

import { getEffectiveBookingStatus } from '../../utils/bookingActionability';
import { Kpi, cx, formatEuro } from '../EventManagerModal/parts/atoms';

const STATUS_COLOR = {
  Paid:     'bg-emerald-500',
  Approved: 'bg-blue-500',
  Pending:  'bg-amber-500',
  Rejected: 'bg-rose-400',
  Open:     'bg-gray-200',
};

const STATUS_TILE = {
  Paid:     'bg-emerald-50 text-emerald-800 ring-emerald-200',
  Approved: 'bg-blue-50 text-blue-800 ring-blue-200',
  Pending:  'bg-amber-50 text-amber-800 ring-amber-200',
  Rejected: 'bg-rose-50 text-rose-700 ring-rose-200',
  Open:     'bg-gray-50 text-gray-500 ring-gray-200',
};

const deriveZone = (space) => {
  if (space?.zone) return String(space.zone).toUpperCase();
  const name = String(space?.name || '');
  const m = name.match(/^([A-Za-z]+)/);
  return m ? m[1].toUpperCase() : '—';
};

// Cancelled / expired bookings release the space, so they never claim a slot
// here — a space whose only booking was cancelled reads as Open.
const SPACE_STATUS_RANK = { Rejected: 1, Pending: 2, Approved: 3, Paid: 4 };

const spaceStatusForBooking = (b) => {
  const s = getEffectiveBookingStatus(b);
  const pay = (b?.paymentStatus || '').toLowerCase();
  if (s === 'paid') return 'Paid';
  if (s === 'approved' && pay === 'paid') return 'Paid';
  if (s === 'approved') return 'Approved';
  if (s === 'pending') return 'Pending';
  if (s === 'rejected') return 'Rejected';
  return null;
};

const statusForSpace = (space, bookingsBySpaceId) => {
  if (space?.status) return space.status;
  return bookingsBySpaceId.get(space?.id) || 'Open';
};

function SummaryCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-4">
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </p>
      <p
        className={cx(
          'text-2xl font-bold mt-1 tabular-nums',
          accent || 'text-gray-900'
        )}
      >
        {value}
      </p>
    </div>
  );
}

const SpacesOverviewPanel = ({ event, spaces = [], bookings = [] }) => {
  const { t } = useTranslation();

  const hasFloorplan = useMemo(
    () =>
      Boolean(event?.floorplanUrl) &&
      Array.isArray(event?.placements) &&
      event.placements.length > 0,
    [event?.floorplanUrl, event?.placements]
  );

  // 0 = Floorplan (when available), 1 = Statistics
  const [subTab, setSubTab] = useState(hasFloorplan ? 0 : 1);
  useEffect(() => {
    setSubTab(hasFloorplan ? 0 : 1);
  }, [hasFloorplan]);

  // ---- KPI strip data (formerly in the modal header) ----
  const totalSpacesHdr = Number(event?.totalSpaces) || 0;
  const confirmedSpacesHdr =
    Number(event?.bookedSpaces ?? event?.confirmedSpaces) || 0;
  const confirmedPct =
    totalSpacesHdr > 0
      ? Math.round((confirmedSpacesHdr / totalSpacesHdr) * 100)
      : 0;
  const attendees = Number(event?.attendeesExpected) || null;

  const pendingCount = useMemo(
    () => bookings.filter((b) => b?.status === 'Pending').length,
    [bookings]
  );

  const revenue = useMemo(
    () =>
      bookings.reduce((sum, b) => {
        if (b?.status !== 'Paid') return sum;
        const price = Number(b?.Space?.price ?? b?.space?.price);
        return Number.isFinite(price) ? sum + price : sum;
      }, 0),
    [bookings]
  );

  // ---- Statistics tab data ----
  // spaceId → strongest claim on that space. A space can carry several bookings
  // over its life (cancel-then-rebook), so last-write-wins would let a stale row
  // mask a paid one and understate occupancy.
  const bookingsBySpaceId = useMemo(() => {
    const m = new Map();
    for (const b of bookings) {
      const id = b?.spaceId ?? b?.Space?.id ?? b?.space?.id;
      if (id == null) continue;
      const status = spaceStatusForBooking(b);
      if (!status) continue;
      const prev = m.get(id);
      if (!prev || SPACE_STATUS_RANK[status] > SPACE_STATUS_RANK[prev]) {
        m.set(id, status);
      }
    }
    return m;
  }, [bookings]);

  const decorated = useMemo(
    () =>
      spaces.map((s) => ({
        ...s,
        zone: deriveZone(s),
        status: statusForSpace(s, bookingsBySpaceId),
      })),
    [spaces, bookingsBySpaceId]
  );

  const total = decorated.length;

  const byStatus = useMemo(() => {
    const acc = { Paid: 0, Approved: 0, Pending: 0, Rejected: 0, Open: 0 };
    for (const s of decorated) acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, [decorated]);

  const confirmedCount = byStatus.Paid + byStatus.Approved;

  const projectedRevenue = useMemo(
    () =>
      decorated
        .filter((s) => s.status === 'Paid' || s.status === 'Approved')
        .reduce((sum, s) => sum + (Number(s.price) || 0), 0),
    [decorated]
  );

  const zones = useMemo(() => {
    const map = {};
    for (const s of decorated) {
      (map[s.zone] = map[s.zone] || []).push(s);
    }
    return map;
  }, [decorated]);

  const subTabs = [
    {
      key: 'floorplan',
      label: t('dashboard.floorplan_tab', 'Floorplan'),
      disabled: !hasFloorplan,
      disabledHint: t(
        'dashboard.floorplan_locked_tab',
        'No floorplan uploaded for this event.'
      ),
    },
    {
      key: 'statistics',
      label: t('dashboard.statistics_tab', 'Statistics'),
    },
  ];

  return (
    <div className="w-full max-w-5xl mx-auto space-y-5">
      {/* KPI strip — moved here from the modal header */}
      <div className="rounded-2xl bg-gradient-to-br from-brand-softer via-white to-white ring-1 ring-black/5 shadow-sm px-3 py-3 flex gap-2">
        <Kpi
          label={t('dashboard.em.kpi_bookings')}
          value={`${confirmedSpacesHdr}/${totalSpacesHdr}`}
          sub={t('dashboard.em.kpi_bookings_sub', {
            count: confirmedPct,
            pct: confirmedPct,
          })}
        />
        <Kpi
          label={t('dashboard.em.kpi_revenue')}
          value={formatEuro(revenue)}
          sub={t('dashboard.em.kpi_revenue_sub')}
        />
        <Kpi
          label={t('dashboard.em.kpi_pending')}
          value={pendingCount}
          sub={t('dashboard.em.kpi_pending_sub')}
        />
        {attendees != null && (
          <Kpi
            label={t('dashboard.em.kpi_rsvps')}
            value={attendees.toLocaleString()}
            sub={t('dashboard.em.kpi_rsvps_sub')}
          />
        )}
      </div>

      {/* Sub-tabs (legacy structure: Floorplan | Statistics) */}
      <div className="flex justify-center">
        <div className="inline-flex gap-1 bg-gray-100 rounded-full p-1 ring-1 ring-black/5">
          {subTabs.map((tab, i) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => !tab.disabled && setSubTab(i)}
              disabled={tab.disabled}
              title={tab.disabled ? tab.disabledHint : undefined}
              className={cx(
                'px-5 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition outline-none',
                subTab === i
                  ? 'bg-brand text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800',
                tab.disabled &&
                  'opacity-50 cursor-not-allowed hover:text-gray-600'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Floorplan sub-tab */}
      {subTab === 0 && hasFloorplan && (
        <PromoterFloorplanView event={event} bookings={bookings} />
      )}

      {/* Statistics sub-tab */}
      {subTab === 1 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label={t('dashboard.em.places_total')} value={total} />
            <SummaryCard
              label={t('dashboard.em.places_confirmed')}
              value={confirmedCount}
              accent="text-emerald-600"
            />
            <SummaryCard
              label={t('dashboard.em.places_open')}
              value={byStatus.Open || 0}
              accent="text-gray-600"
            />
            <SummaryCard
              label={t('dashboard.em.places_revenue')}
              value={formatEuro(projectedRevenue)}
            />
          </div>

          {total > 0 && (
            <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-900">
                  {t('dashboard.em.places_status_title')}
                </h4>
                <span className="text-xs text-gray-400">
                  {t('dashboard.em.places_total_count', { count: total })}
                </span>
              </div>
              <div className="h-3 rounded-full bg-gray-100 overflow-hidden flex">
                {Object.entries(byStatus).map(
                  ([k, v]) =>
                    v > 0 && (
                      <div
                        key={k}
                        className={cx('h-full', STATUS_COLOR[k])}
                        style={{ width: `${(v / total) * 100}%` }}
                        title={`${k}: ${v}`}
                      />
                    )
                )}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-xs">
                {Object.entries(byStatus)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center gap-1.5 text-gray-600"
                    >
                      <span
                        className={cx('w-2 h-2 rounded-full', STATUS_COLOR[k])}
                      />
                      <span className="font-medium">
                        {t(`dashboard.em.status_pill.${k}`, k)}
                      </span>
                      <span className="text-gray-400">· {v}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">
                {t('dashboard.em.places_by_zone')}
              </h4>
            </div>
            {total === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                {t('dashboard.em.no_bookings_in_view')}
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(zones)
                  .sort()
                  .map(([zone, list]) => (
                    <div key={zone}>
                      <div className="flex items-baseline justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {t('dashboard.em.places_zone', { zone })}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t('dashboard.em.places_spaces', {
                            count: list.length,
                          })}
                        </p>
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {list.map((s) => (
                          <div
                            key={s.id || s.name}
                            title={`${s.name || s.id} · ${formatEuro(s.price || 0)} · ${s.status}`}
                            className={cx(
                              'aspect-square rounded-lg flex flex-col items-center justify-center p-1 text-[10px] font-semibold ring-1 transition cursor-default hover:scale-[1.04]',
                              STATUS_TILE[s.status] || STATUS_TILE.Open
                            )}
                          >
                            <span className="tabular-nums truncate max-w-full">
                              {s.name || s.id}
                            </span>
                            {Number(s.price) > 0 && (
                              <span className="text-[9px] opacity-70 font-medium mt-0.5">
                                {formatEuro(s.price)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SpacesOverviewPanel;
